import { createCardHTML } from '../game/cardHtml';

type ShowBonusContext = {
  state?: any;
  t?: (key: string, fallback?: string) => string;
  beatlineLang?: string;
  suspenseLines?: { nl?: string[]; en?: string[] };
  beatlineSetBonusOpen?: (open: boolean) => void;
  beatlineRestartPopupAnimation?: (el: Element | null) => void;
  beatlinePrepareBonusModalCardText?: (el: Element | null) => void;
  beatlineBonusQuestionText?: (playerId: number) => string;
  pauseYouTube?: () => void;
  pauseItunes?: () => void;
};

type ShowBonusOptions = {
  autoCloseMs?: number;
  onAutoClose?: () => void;
  effectFn?: () => void;
  enableSuspense?: boolean;
  suspenseChance?: number;
  minDelayMs?: number;
  extraDelayMs?: number;
  delayMs?: number;
  modalClass?: string;
  bonusPlayerId?: number | string;
};

function safeCall(fn?: Function, ...args: unknown[]): void {
  try { if (typeof fn === 'function') fn(...args); } catch (_) {}
}

function restartPopupAnimation(el: Element | null, ctx: ShowBonusContext): void {
  if (typeof ctx.beatlineRestartPopupAnimation === 'function') {
    safeCall(ctx.beatlineRestartPopupAnimation, el);
    return;
  }
  const htmlEl = el as HTMLElement | null;
  if (!htmlEl) return;
  try {
    htmlEl.style.animation = 'none';
    void htmlEl.offsetHeight;
    htmlEl.style.animation = '';
  } catch (_) {}
}

function defaultBonusQuestion(playerId: number): string {
  const lang = String((window as any).beatlineLang || 'nl');
  const label = lang === 'en' ? 'Player' : 'Speler';
  const suffix = lang === 'en' ? 'title & artist correct?' : 'Titel & artiest goed?';
  return `${label} ${playerId}: ${suffix}`;
}

function prepareCardText(cardView: Element | null, ctx: ShowBonusContext): void {
  if (typeof ctx.beatlinePrepareBonusModalCardText === 'function') {
    safeCall(ctx.beatlinePrepareBonusModalCardText, cardView);
  }
}

function suspenseLine(ctx: ShowBonusContext): string {
  const lang = String(ctx.beatlineLang || (window as any).beatlineLang || 'nl');
  const fallback = {
    nl: ['Even kijken...', 'Spannend...', 'Momentje...'],
    en: ['Checking...', 'Suspense...', 'One moment...']
  };
  const source = ctx.suspenseLines || (window as any).BEATLINE_SUSPENSE_LINES || fallback;
  const lines = (lang === 'en' ? source.en : source.nl) || fallback.nl;
  return lines[Math.floor(Math.random() * lines.length)] || fallback.nl[0];
}


function restoreDefaultSpecialNextButton(ctx: ShowBonusContext): void {
  const wrap = document.getElementById('bonus-timeattack-btns') as HTMLElement | null;
  if (!wrap || wrap.dataset.beatlineMode !== 'perfect-board') return;
  const nextLabel = typeof ctx.t === 'function' ? ctx.t('btn_next', 'VOLGENDE') : 'VOLGENDE';
  wrap.dataset.beatlineMode = '';
  wrap.innerHTML = `<button class="btn btn-main" onclick="timeAttackContinue()">${nextLabel}</button>`;
}

function buildPlaylistPayload(state: any): Record<string, any> {
  const ids = Array.isArray(state?.playlistIds) && state.playlistIds.length
    ? state.playlistIds.map((id: any) => String(id)).filter(Boolean)
    : [state?.playlistId || (window as any).currentPlaylistId || (document.getElementById('playlist-select') as HTMLSelectElement | null)?.value || ''].map(String).filter(Boolean);
  const out: Record<string, any> = { playlist_id: ids[0] || '' };
  if (ids.length > 1) out.playlist_ids = ids;
  return out;
}

async function fetchPerfectOrderMeta(ids: string[], state: any): Promise<Map<string, any>> {
  const map = new Map<string, any>();
  const unique = Array.from(new Set(ids.map(String).filter(Boolean)));
  if (!unique.length) return map;

  const existing = Array.isArray(state?.perfectCurrentCards) ? state.perfectCurrentCards : [];
  existing.forEach((song: any) => {
    if (song && song.id && song.title && song.year) map.set(String(song.id), song);
  });

  const missing = unique.filter((id) => !map.has(id));
  if (!missing.length) return map;

  try {
    const ajaxPost = (window as any).ajaxPost;
    if (typeof ajaxPost !== 'function') return map;
    const resp = await ajaxPost('beatline_get_song_meta', {
      ...buildPlaylistPayload(state),
      mode: state?.mode || 'spotify',
      ids: missing
    });
    const songs = resp?.success && resp?.data && Array.isArray(resp.data.songs) ? resp.data.songs : [];
    songs.forEach((song: any) => {
      if (song && song.id) map.set(String(song.id), song);
    });

    if (Array.isArray(state?.perfectCurrentCards)) {
      state.perfectCurrentCards = state.perfectCurrentCards.map((song: any) => {
        const full = song && song.id ? map.get(String(song.id)) : null;
        return full ? { ...song, ...full } : song;
      });
    }
  } catch (_) {}

  return map;
}

async function revealPerfectOrderBoardForReview(state: any): Promise<void> {
  const timeline = document.getElementById('timeline-p1');
  if (!timeline || !(state && state.perfectOrder)) return;

  const source = Array.isArray(state.perfectCurrentCards) ? state.perfectCurrentCards : [];
  const cards = Array.from(timeline.querySelectorAll<HTMLElement>('.card'));
  const ids = cards.map((card) => String(card.dataset.id || '')).filter(Boolean);
  const metaById = await fetchPerfectOrderMeta(ids, state);

  const cardsById = new Map<string, any>();
  source.forEach((song: any) => {
    if (song && song.id) cardsById.set(String(song.id), song);
  });

  cards.forEach((card, index) => {
    const id = String(card.dataset.id || '');
    const fallbackSong = cardsById.get(id) || source.find((item: any) => String(item?.id || '') === id) || {};
    const song = metaById.get(id) || fallbackSong || { id, title: card.dataset.title || '', year: card.dataset.year || '' };
    const classes = Array.from(card.classList).filter((className) => /^po-c\d+$/.test(className));
    const wrapper = document.createElement('div');
    wrapper.innerHTML = createCardHTML({ ...song, id: id || song.id }, true).trim();
    const revealed = wrapper.firstElementChild as HTMLElement | null;
    if (!revealed) {
      card.classList.add('revealed');
      return;
    }
    classes.forEach((className) => revealed.classList.add(className));
    if (!classes.length) revealed.classList.add(`po-c${(index % 6) + 1}`);
    revealed.dataset.id = id || String(song.id || '');
    card.replaceWith(revealed);
  });

  try {
    const applyColors = (window as any).beatlineTsApplyPerfectOrderCardColors;
    if (typeof applyColors === 'function') applyColors(timeline);
  } catch (_) {}
}

function showPerfectOrderBoardContinueButton(ctx: ShowBonusContext): void {
  const lang = String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase();
  const btn = document.getElementById('btn-next') as HTMLButtonElement | null;
  if (!btn) return;
  const continueLabel = typeof ctx.t === 'function'
    ? ctx.t('btn_next', lang === 'en' ? 'NEXT' : 'VERDER')
    : (lang === 'en' ? 'NEXT' : 'VERDER');
  btn.dataset.beatlinePerfectContinue = '1';
  btn.textContent = continueLabel;
  btn.disabled = false;
  btn.style.display = 'flex';
  btn.onclick = (ev) => {
    try { ev.preventDefault(); } catch (_) {}
    btn.style.display = 'none';
    const cont = (window as any).timeAttackContinue;
    if (typeof cont === 'function') cont();
  };
}

function configurePerfectOrderResultButtons(state: any, ctx: ShowBonusContext): void {
  const wrap = document.getElementById('bonus-timeattack-btns') as HTMLElement | null;
  if (!wrap) return;

  if (!(state && state.perfectOrder && state.perfectAwaitingNext)) {
    restoreDefaultSpecialNextButton(ctx);
    return;
  }

  const lang = String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase();
  const viewLabel = typeof ctx.t === 'function'
    ? ctx.t('view_board', lang === 'en' ? 'View board' : 'Bekijk bord')
    : (lang === 'en' ? 'View board' : 'Bekijk bord');
  const continueLabel = typeof ctx.t === 'function'
    ? ctx.t('btn_next', lang === 'en' ? 'NEXT' : 'VERDER')
    : (lang === 'en' ? 'NEXT' : 'VERDER');

  wrap.dataset.beatlineMode = 'perfect-board';
  wrap.innerHTML = `
    <button type="button" class="btn btn-ghost" data-beatline-perfect-view-board="1">${viewLabel}</button>
    <button type="button" class="btn btn-main" data-beatline-perfect-continue="1">${continueLabel}</button>
  `;

  const viewBtn = wrap.querySelector<HTMLButtonElement>('[data-beatline-perfect-view-board]');
  const continueBtn = wrap.querySelector<HTMLButtonElement>('[data-beatline-perfect-continue]');
  if (viewBtn) {
    viewBtn.onclick = async () => {
      viewBtn.disabled = true;
      try {
        await revealPerfectOrderBoardForReview(state);
        closeModalFromTypeScript('bonus-modal', ctx);
        showPerfectOrderBoardContinueButton(ctx);
      } finally {
        viewBtn.disabled = false;
      }
    };
  }
  if (continueBtn) {
    continueBtn.onclick = () => {
      const cont = (window as any).timeAttackContinue;
      if (typeof cont === 'function') cont();
    };
  }
}

export function setBonusOpen(open: boolean): void {
  try {
    const app = document.getElementById('beatline-app');
    if (app) app.classList.toggle('bonus-open', !!open);
  } catch (_) {}
}

export function closeModalFromTypeScript(id: string, ctx: ShowBonusContext = {}): { handled: boolean } {
  const el = document.getElementById(id);
  if (el) (el as HTMLElement).style.display = 'none';

  if (id === 'bonus-modal' || id === 'profile-modal') {
    try {
      if (id === 'bonus-modal' && (window as any).__bonusAutoCloseTimer) {
        clearTimeout((window as any).__bonusAutoCloseTimer);
        (window as any).__bonusAutoCloseTimer = null;
      }
      if (id === 'bonus-modal' && (window as any).bonusAutoCloseTimer) {
        clearTimeout((window as any).bonusAutoCloseTimer);
        (window as any).bonusAutoCloseTimer = null;
      }
      if (typeof ctx.beatlineSetBonusOpen === 'function') ctx.beatlineSetBonusOpen(false);
      else setBonusOpen(false);
    } catch (_) {}
  }

  return { handled: true };
}

export function showBonusModalFromTypeScript(
  headerText: string,
  subMsg: string,
  status: string,
  opts: ShowBonusOptions = {},
  ctx: ShowBonusContext = {}
): { handled: boolean } {
  const state = ctx.state || (window as any).state || {};
  const modal = document.getElementById('bonus-modal') as HTMLElement | null;
  if (!modal) return { handled: false };

  const content = modal.querySelector('.glass-card-large') as HTMLElement | null;
  const iconArea = document.getElementById('bonus-status-icon') as HTMLElement | null;
  const headerEl = document.getElementById('bonus-header') as HTMLElement | null;
  const msgEl = document.getElementById('bonus-msg') as HTMLElement | null;
  const cardView = document.querySelector('#bonus-modal .modal-card-view') as HTMLElement | null;
  const bonusQuestionEl = document.getElementById('bonus-token-question') as HTMLElement | null;

  if (!content || !iconArea || !headerEl || !msgEl) return { handled: false };

  const autoCloseMs = typeof opts.autoCloseMs === 'number' ? opts.autoCloseMs : 0;
  const onAutoClose = typeof opts.onAutoClose === 'function' ? opts.onAutoClose : null;

  if ((window as any).__bonusAutoCloseTimer) {
    clearTimeout((window as any).__bonusAutoCloseTimer);
    (window as any).__bonusAutoCloseTimer = null;
  }

  if (state.mode === 'youtube') safeCall(ctx.pauseYouTube);
  if (state.mode === 'itunes') {
    if (typeof ctx.pauseItunes === 'function') safeCall(ctx.pauseItunes);
    else {
      const audio = document.getElementById('itunes-audio') as HTMLAudioElement | null;
      try { audio?.pause(); } catch (_) {}
    }
  }

  if (bonusQuestionEl) {
    const bonusPlayerId = (opts && parseInt(String(opts.bonusPlayerId || 0), 10)) || parseInt(String(state.turn || 0), 10) || 0;
    if (bonusPlayerId > 0) {
      bonusQuestionEl.dataset.beatlineBonusPlayerId = String(bonusPlayerId);
      bonusQuestionEl.textContent = typeof ctx.beatlineBonusQuestionText === 'function'
        ? ctx.beatlineBonusQuestionText(bonusPlayerId)
        : defaultBonusQuestion(bonusPlayerId);
    }
  }

  const effectFn = typeof opts.effectFn === 'function' ? opts.effectFn : null;
  const enableSuspense = opts.enableSuspense === false ? false : true;
  const eligibleStatus = (status === 'correct' || status === 'wrong');
  const eligible = enableSuspense && (parseInt(String(state.playerCount || 0), 10) > 1) && eligibleStatus;
  const chance = typeof opts.suspenseChance === 'number' ? opts.suspenseChance : 0.5;
  const doSuspense = eligible && (Math.random() < chance);
  const baseDelay = typeof opts.minDelayMs === 'number' ? opts.minDelayMs : 2100;
  const extraDelay = typeof opts.extraDelayMs === 'number' ? opts.extraDelayMs : 1600;
  const delayMs = typeof opts.delayMs === 'number' ? opts.delayMs : (baseDelay + Math.floor(Math.random() * extraDelay));

  if (cardView) {
    cardView.innerHTML = state.activeCard ? createCardHTML(state.activeCard, true) : '';
    prepareCardText(cardView, ctx);
  }

  const renderStatus = (h: string, m: string, st: string) => {
    content.classList.remove('modal-correct', 'modal-wrong', 'modal-stolen', 'modal-suspense', 'modal-swap');
    modal.classList.remove('reveal-suspense');
    headerEl.classList.remove('reveal-pulse');
    msgEl.classList.remove('reveal-dots');

    if (st === 'correct') {
      content.classList.add('modal-correct');
      iconArea.innerHTML = '<div class="status-circle bg-check">✓</div>';
    } else if (st === 'wrong') {
      content.classList.add('modal-wrong');
      iconArea.innerHTML = '<div class="status-circle bg-cross">✕</div>';
    } else if (st === 'stolen') {
      content.classList.add('modal-stolen');
      iconArea.innerHTML = '<div class="status-circle bg-stolen">⚔️</div>';
    } else {
      iconArea.innerHTML = '';
    }

    if (opts && opts.modalClass === 'modal-swap') content.classList.add('modal-swap');

    headerEl.innerText = h || '';
    msgEl.innerText = m || '';

    if (autoCloseMs && autoCloseMs > 0) {
      (window as any).__bonusAutoCloseTimer = window.setTimeout(() => {
        if (modal && modal.style.display !== 'none') {
          modal.style.display = 'none';
          if (typeof ctx.beatlineSetBonusOpen === 'function') ctx.beatlineSetBonusOpen(false);
          else setBonusOpen(false);
          if (onAutoClose) onAutoClose();
        }
        (window as any).__bonusAutoCloseTimer = null;
      }, autoCloseMs);
    }
  };

  const renderSuspense = () => {
    content.classList.remove('modal-correct', 'modal-wrong', 'modal-stolen');
    content.classList.add('modal-suspense');
    iconArea.innerHTML = '<div class="status-circle bg-suspense">?</div>';
    headerEl.innerText = suspenseLine(ctx);
    headerEl.classList.add('reveal-pulse');
    msgEl.innerText = '';
    msgEl.classList.add('reveal-dots');
    modal.classList.add('reveal-suspense');
  };

  if ((window as any).__bonusSuspenseTimer) {
    clearTimeout((window as any).__bonusSuspenseTimer);
    (window as any).__bonusSuspenseTimer = null;
  }

  modal.style.display = 'flex';
  if (typeof ctx.beatlineSetBonusOpen === 'function') ctx.beatlineSetBonusOpen(true);
  else setBonusOpen(true);
  restartPopupAnimation(content, ctx);

  if (doSuspense) {
    renderSuspense();
    (window as any).__bonusSuspenseTimer = window.setTimeout(() => {
      renderStatus(headerText, subMsg, status);
      configurePerfectOrderResultButtons(state, ctx);
      if (effectFn) effectFn();
      (window as any).__bonusSuspenseTimer = null;
    }, delayMs);
    return { handled: true };
  }

  renderStatus(headerText, subMsg, status);
  configurePerfectOrderResultButtons(state, ctx);
  if (effectFn) effectFn();
  return { handled: true };
}
